import { DateOnly } from '@if/core/date'
import { ControlledNumberField, ControlledSelect, ErrorAlert } from '@if/mui'
import { Alert, Box, CircularProgress, Stack } from '@mui/material'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUnmountableDialog } from '@src/services/dialog'
import { ECalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { FormEventHandler, useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import LoanCalendarActionPreviewTable from '../LoanCalendarActionPreview/LoanCalendarActionPreviewTable'
import { useValidPaymentOperationsForCalendarShift } from '../LoanMoveCalendar/LoanMoveCalendar-hooks'
import { ChangeRegularPmtSchemaType, useChangeRegularPmtResolver } from './LoanCalendarChangeRegularPmt-schema'

interface LoanCalendarChangeRegularPmtDialogProps {
  initialSignedSimulation: SignedCalendarSimulation
  onConfirm: (data: SignedCalendarSimulation) => void
  onClose: () => void
  isLockedByOther?: boolean
}

const LoanCalendarChangeRegularPmtDialog = ({
  initialSignedSimulation,
  onConfirm,
  onClose,
  isLockedByOther = false,
}: LoanCalendarChangeRegularPmtDialogProps) => {
  const { t } = useTranslation()
  const [newSimulationWithSignature, setNewSimulationWithSignature] =
    useState<SignedCalendarSimulation>(initialSignedSimulation)

  const {
    mutateAsync: computeAction,
    error,
    isPending,
  } = useComputeCalendarAction(
    initialSignedSimulation.simulation.fullLoan.loan.id,
    initialSignedSimulation.simulation.fullLoan.loan.financingProgramId,
  )

  const { dialogProps, closeDialog } = useUnmountableDialog(onClose, isPending)
  const resolver = useChangeRegularPmtResolver()

  const defaultValues = {
    amount: null,
    fromDate: null,
  } as unknown as ChangeRegularPmtSchemaType

  const {
    control,
    trigger,
    formState: { isValid, isValidating, isSubmitting },
  } = useForm({
    resolver,
    mode: 'onChange',
    defaultValues,
    context: { simulation: initialSignedSimulation.simulation },
  })

  const originalSimulation = initialSignedSimulation.simulation
  const paymentOptions = useValidPaymentOperationsForCalendarShift(originalSimulation)
  const [amount, fromDate] = useWatch({ control, name: ['amount', 'fromDate'] })

  useEffect(() => {
    if (fromDate) void trigger('amount')
  }, [fromDate, trigger])

  useEffect(() => {
    if (isValid && !isPending && !isValidating && !isSubmitting) {
      const timeoutId = setTimeout(() => {
        void computeAction({
          ...initialSignedSimulation,
          action: {
            actionType: ECalendarSimulationAction.ChangeRegularPmt,
            amount,
            fromDate,
          },
        }).then((data) => {
          setNewSimulationWithSignature(data)
        })
      }, 1000)

      return () => clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, fromDate, isValid, isValidating])

  const onSubmitForm: FormEventHandler = (e) => {
    e.preventDefault()
    if (!isPending) {
      onConfirm(newSimulationWithSignature)
      closeDialog()
    }
  }

  return (
    <FormDialog
      {...dialogProps}
      onConfirm={onSubmitForm}
      title={t('loan.payment.changeRegularPmtDialog.title')}
      disabled={isPending || isLockedByOther || !isValid}
    >
      {error && <ErrorAlert error={error} />}

      <Stack spacing={2}>
        <Box position="relative">
          <LoanCalendarActionPreviewTable
            simulationBefore={originalSimulation}
            simulationAfter={newSimulationWithSignature.simulation}
            actionDate={fromDate ?? undefined}
          />
          {isPending && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          )}
        </Box>
        <Stack direction="row" spacing={2}>
          <ControlledNumberField
            control={control}
            name="amount"
            label={t('loan.payment.changeRegularPmtDialog.amount')}
            disabled={isPending || isLockedByOther}
          />
          <ControlledSelect
            name="fromDate"
            control={control}
            options={paymentOptions}
            parseValue={(v) => (v ? new DateOnly(v) : null)}
            label={t('loan.payment.changeRegularPmtDialog.fromDate')}
            disableEmptyOption
            disabled={isPending || isLockedByOther}
          />
        </Stack>
        <Alert severity="warning">{t('loan.payment.changeRegularPmtDialog.warning')}</Alert>
      </Stack>
    </FormDialog>
  )
}

export default LoanCalendarChangeRegularPmtDialog
